import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { buildHomeDashboardChartPreviewSlotStyle } from './lib/resolveHomeDashboardChartHeight';
import HomeDashboardChartLoading from './HomeDashboardChartLoading';
import { useGetOverdueStatDataQuery } from '../../slices/dashboardApiSlice';
import { useGetStatisticsDashboardChartsQuery } from '../../slices/statisticsApiSlice';
import {
	getHomeDashboardChartColSpanCycle,
	isPipelineBuiltinChartKind,
	PERSISTED_CHART_KIND,
	resolveHomeDashboardChartColSpan,
	resolvePersistedChartKind,
} from '../statistics/charts/board/lib/statisticsPersistedCharts';
import { isFrozenChartSnapshot } from '../statistics/charts/board/lib/statisticsChartRefreshMode';
import DashboardChartCard from '../statistics/charts/board/ui/DashboardChartCard';
import useDashboardChartsBatch from '../statistics/charts/board/hooks/useDashboardChartsBatch';
import useDashboardChartsGridInteractions from '../statistics/charts/board/hooks/useDashboardChartsGridInteractions';
import createDashboardChartDragGhost from './lib/createDashboardChartDragGhost';
import { DASHBOARD_DRAG_APPEND_TARGET_ID } from '../../utils/dashboardDragReorder';
import { HOME_DASHBOARD_WIDE_LAYOUT_MIN_PX } from '../../utils/dashboard/homeDashboardLayout';
import HomeDashboardChartsFullscreenModal from './HomeDashboardChartsFullscreenModal';

const EMPTY_INCLUDE_ORDERS_CHART_IDS = [];

const homeChartsShellStorageKey = (userId) => (userId ? `erp-home-charts-shell:${userId}` : null);

const readHomeChartsShell = (userId) => {
	const key = homeChartsShellStorageKey(userId);
	if (!key || typeof window === 'undefined') return [];
	try {
		const parsed = JSON.parse(window.sessionStorage.getItem(key) || '[]');
		return Array.isArray(parsed) ? parsed.filter((item) => item?.id && item?.title) : [];
	} catch {
		return [];
	}
};

const writeHomeChartsShell = (userId, charts) => {
	const key = homeChartsShellStorageKey(userId);
	if (!key || typeof window === 'undefined' || !Array.isArray(charts) || charts.length === 0) return;
	try {
		const shell = charts.slice(0, 4).map((chart) => ({
			id: chart.id,
			title: chart.title || '经营图表',
			colSpan: chart.ui?.colSpan ?? null,
		}));
		window.sessionStorage.setItem(key, JSON.stringify(shell));
	} catch {
		// ignore quota / private mode
	}
};

const HomeDashboardChartsGrid = ({
	charts = [],
	onDelete,
	onRename,
	onReorder,
	onResizeColSpan,
	onOpenChartWorkbench,
	isManager = false,
	maxCharts = 4,
	actionMessage = '',
	onEditSaved,
	isChartsLoading = false,
	dataRefreshToken = 0,
	userId = null,
}) => {
	const { breakpoints, width } = useBreakpoints();
	const needsOverdueStats = useMemo(
		() => charts.some((chart) => resolvePersistedChartKind(chart) === PERSISTED_CHART_KIND.OVERDUE),
		[charts],
	);
	const [fullscreenChartId, setFullscreenChartId] = useState(null);
	const [modalOrdersChartId, setModalOrdersChartId] = useState(null);
	const [shellCharts] = useState(() => readHomeChartsShell(userId));
	const includeOrdersChartIds = useMemo(() => (modalOrdersChartId ? [modalOrdersChartId] : EMPTY_INCLUDE_ORDERS_CHART_IDS), [modalOrdersChartId]);
	const fullscreenOverdueNeedsDetails = useMemo(() => {
		if (!fullscreenChartId) return false;
		const chart = charts.find((item) => item.id === fullscreenChartId);
		return Boolean(chart && resolvePersistedChartKind(chart) === PERSISTED_CHART_KIND.OVERDUE);
	}, [charts, fullscreenChartId]);
	const {
		data: overdueSummary = {},
		isLoading: isOverdueSummaryLoading,
		refetch: refetchOverdueSummary,
	} = useGetOverdueStatDataQuery({ summaryOnly: true }, { skip: !isManager || !needsOverdueStats });
	const {
		data: overdueDetails,
		isLoading: isOverdueDetailsLoading,
		refetch: refetchOverdueDetails,
	} = useGetOverdueStatDataQuery(undefined, {
		skip: !isManager || !needsOverdueStats || !fullscreenOverdueNeedsDetails,
	});
	const overdueData = fullscreenOverdueNeedsDetails && overdueDetails ? overdueDetails : overdueSummary;
	const isOverdueLoading = fullscreenOverdueNeedsDetails ? isOverdueDetailsLoading : isOverdueSummaryLoading;

	useEffect(() => {
		if (!isManager || !needsOverdueStats || dataRefreshToken <= 0) return;
		refetchOverdueSummary();
		if (fullscreenOverdueNeedsDetails) refetchOverdueDetails();
	}, [dataRefreshToken, fullscreenOverdueNeedsDetails, isManager, needsOverdueStats, refetchOverdueDetails, refetchOverdueSummary]);
	const needsStatisticsSourceFallback = useMemo(
		() =>
			charts.some(
				(chart) =>
					chart?.sourceStatisticsChartId &&
					!(chart.liveDisplayFallback && typeof chart.liveDisplayFallback === 'object') &&
					!(isFrozenChartSnapshot(chart) && chart.dataSnapshot),
			),
		[charts],
	);
	const { data: statisticsDashboardChartsData } = useGetStatisticsDashboardChartsQuery(undefined, {
		skip: !isManager || charts.length === 0 || !needsStatisticsSourceFallback,
	});
	const statisticsSourceSnapshotsById = useMemo(() => {
		const map = {};
		for (const chart of statisticsDashboardChartsData?.charts ?? []) {
			if (chart?.id) map[chart.id] = chart;
		}
		return map;
	}, [statisticsDashboardChartsData?.charts]);
	const { bundleByChartId } = useDashboardChartsBatch(charts, {
		isManager,
		overdueData,
		isOverdueLoading,
		enabled: charts.length > 0,
		dataRefreshToken,
		includeOrdersChartIds,
		statisticsSourceSnapshotsById,
		fetchCustomers: false,
		// 首页卡片先出主序列；同比/环比扫描延后（全屏/统计页仍会带 compares）
		includeCompares: false,
	});
	const useCompactChartGrid = breakpoints.down('lg') || width >= HOME_DASHBOARD_WIDE_LAYOUT_MIN_PX;
	const gridColumns = breakpoints.down('md') ? 1 : useCompactChartGrid ? 2 : 4;
	const gridRef = useRef(null);

	useEffect(() => {
		writeHomeChartsShell(userId, charts);
	}, [charts, userId]);

	const chartIds = useMemo(() => charts.map((chart) => chart.id), [charts]);

	const {
		canResizeWidth,
		canReorder,
		draggingId,
		dragOverId,
		resizingColSpan,
		handleDragStart,
		handleDragEnd,
		handleDragOver,
		handleAppendDragOver,
		handleAppendDrop,
		handleItemDragLeave,
		handleGridDragLeave,
		handleDrop,
		handleCycleColSpan,
		handleResizePointerDown,
		resolveDisplayColSpan,
	} = useDashboardChartsGridInteractions({
		gridRef,
		gridColumns,
		chartCount: charts.length,
		orderedIds: chartIds,
		onReorder,
		onResizeColSpan,
		getColSpanCycle: getHomeDashboardChartColSpanCycle,
		draggingBodyClass: 'dashboard-home-charts--dragging',
		cardSelector: '.dashboard-home-chart-card',
		createDragGhost: createDashboardChartDragGhost,
	});

	const handleOpenFullscreen = useCallback((chartId) => {
		setFullscreenChartId(chartId);
	}, []);

	const handleCloseFullscreen = useCallback(() => {
		setFullscreenChartId(null);
		setModalOrdersChartId(null);
	}, []);

	const handleModalDrillChange = useCallback(
		(drill) => {
			if (drill && fullscreenChartId) {
				setModalOrdersChartId(fullscreenChartId);
				return;
			}
			setModalOrdersChartId(null);
		},
		[fullscreenChartId],
	);

	const handleOpenEdit = useCallback(
		(snapshot) => {
			const kind = resolvePersistedChartKind(snapshot);
			if (isPipelineBuiltinChartKind(kind)) return;
			onOpenChartWorkbench?.(snapshot);
		},
		[onOpenChartWorkbench],
	);

	const resolveCardEditHandler = useCallback(
		(snapshot) => {
			if (!onOpenChartWorkbench) return undefined;
			const kind = resolvePersistedChartKind(snapshot);
			if (isPipelineBuiltinChartKind(kind)) return undefined;
			return handleOpenEdit;
		},
		[handleOpenEdit, onOpenChartWorkbench],
	);

	if (isChartsLoading && charts.length === 0) {
		const usingShell = shellCharts.length > 0;
		const placeholderCharts = usingShell
			? shellCharts
			: Array.from({ length: Math.min(Math.max(gridColumns, 2), maxCharts) }, (_, index) => ({
					id: `home-charts-loading-${index}`,
					title: '',
					colSpan: null,
				}));
		const placeholderCount = placeholderCharts.length;
		return (
			<section
				className='statistics-persisted-charts statistics-persisted-charts--home-dashboard statistics-persisted-charts--loading'
				aria-label='经营看板'
				aria-busy='true'
			>
				<div
					className={[
						'statistics-persisted-charts__grid',
						'statistics-persisted-charts__grid--home',
						`statistics-persisted-charts__grid--n-${placeholderCount}`,
					].join(' ')}
					style={{ '--home-dashboard-grid-columns': gridColumns }}
				>
					{placeholderCharts.map((item, index) => {
						const loadingColSpan = Math.min(
							gridColumns,
							Number.isFinite(Number(item.colSpan)) && Number(item.colSpan) > 0 ? Number(item.colSpan) : Math.max(1, Math.floor(gridColumns / placeholderCount) || 1),
						);
						return (
							<div
								key={item.id}
								className={[
									'statistics-persisted-charts__item',
									'statistics-persisted-charts__item--tile-compact',
									`statistics-persisted-charts__item--cols-${loadingColSpan}`,
								].join(' ')}
							>
								<article className='card dashboard-home-chart-card dashboard-home-chart-card--loading-placeholder'>
									<header className='card-header dashboard-home-chart-card__header'>
										<div className='dashboard-home-chart-card__head-main'>
											{item.title ? (
												<h4 className='dashboard-home-chart-card__title dashboard-stat-card__title mb-0'>{item.title}</h4>
											) : (
												<span className='dashboard-stat-card__skeleton dashboard-home-chart-loading__title' aria-hidden='true' />
											)}
										</div>
									</header>
									<div className='card-body dashboard-home-chart-card__body'>
										<div
											className='dashboard-home-chart-card__preview dashboard-home-chart-card__preview--fixed-slot dashboard-home-chart-card__preview--skeleton'
											style={buildHomeDashboardChartPreviewSlotStyle()}
										>
											<HomeDashboardChartLoading label={index === 0 ? '加载经营看板' : '加载图表'} />
										</div>
									</div>
								</article>
							</div>
						);
					})}
				</div>
			</section>
		);
	}

	if (!charts.length) {
		const emptyColSpan = Math.min(gridColumns, maxCharts);
		return (
			<section className='statistics-persisted-charts statistics-persisted-charts--home-dashboard statistics-persisted-charts--empty' aria-label='经营看板'>
				<div
					className={['statistics-persisted-charts__grid', 'statistics-persisted-charts__grid--home', 'statistics-persisted-charts__grid--n-1'].join(' ')}
					style={{ '--home-dashboard-grid-columns': gridColumns }}
				>
					<div
						className={[
							'statistics-persisted-charts__item',
							'statistics-persisted-charts__item--tile-compact',
							`statistics-persisted-charts__item--cols-${emptyColSpan}`,
						].join(' ')}
					>
						<article className='card dashboard-home-chart-card dashboard-home-chart-card--loading-placeholder'>
							<header className='card-header dashboard-home-chart-card__header'>
								<div className='dashboard-home-chart-card__head-main'>
									<h4 className='dashboard-home-chart-card__title mb-0 text-muted'>经营看板</h4>
								</div>
							</header>
							<div className='card-body dashboard-home-chart-card__body'>
								<div
									className='dashboard-home-chart-card__preview dashboard-home-chart-card__preview--fixed-slot dashboard-home-chart-card__preview--empty'
									style={buildHomeDashboardChartPreviewSlotStyle()}
								>
									<span className='text-muted small text-center'>
										暂无固定图表，可在
										<Link to='/statistics' className='ms-1'>
											业务统计
										</Link>
										添加
									</span>
								</div>
							</div>
						</article>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section
			className={['statistics-persisted-charts', 'statistics-persisted-charts--home-dashboard', draggingId ? 'is-reordering' : null].filter(Boolean).join(' ')}
			aria-label='经营看板'
		>
			{actionMessage ? <p className='statistics-persisted-charts__message mb-2'>{actionMessage}</p> : null}
			<div
				ref={gridRef}
				className={[
					'statistics-persisted-charts__grid',
					'statistics-persisted-charts__grid--home',
					`statistics-persisted-charts__grid--n-${Math.min(charts.length, maxCharts)}`,
					draggingId ? 'is-reordering' : null,
					resizingColSpan ? 'is-resizing' : null,
				]
					.filter(Boolean)
					.join(' ')}
				style={{ '--home-dashboard-grid-columns': gridColumns }}
				onDragOver={(event) => event.preventDefault()}
				onDragLeave={handleGridDragLeave}
			>
				{charts.map((snapshot, index) => {
					const savedColSpan = resolveHomeDashboardChartColSpan(snapshot, index, charts.length, gridColumns);
					const colSpan = resolveDisplayColSpan(snapshot.id, savedColSpan);
					const nextColSpan = getHomeDashboardChartColSpanCycle(colSpan, gridColumns);
					const showInsertLine = Boolean(draggingId && dragOverId === snapshot.id && draggingId !== snapshot.id);

					return (
						<div
							key={snapshot.id}
							className={[
								'statistics-persisted-charts__item',
								'statistics-persisted-charts__item--tile-compact',
								`statistics-persisted-charts__item--cols-${colSpan}`,
								resizingColSpan?.chartId === snapshot.id ? 'is-resizing-width' : null,
								draggingId === snapshot.id ? 'is-dragging' : null,
								dragOverId === snapshot.id && draggingId !== snapshot.id ? 'is-insert-target' : null,
							]
								.filter(Boolean)
								.join(' ')}
							onDragEnter={(event) => event.preventDefault()}
							onDragOver={(event) => handleDragOver(event, snapshot.id)}
							onDragLeave={(event) => handleItemDragLeave(event, snapshot.id)}
							onDrop={(event) => handleDrop(event, snapshot.id)}
						>
							{showInsertLine ? <div className='dashboard-home-chart-insert-line' aria-hidden='true' /> : null}
							<DashboardChartCard
								snapshot={snapshot}
								chartBundle={bundleByChartId[snapshot.id]}
								useBatchBundle
								onDelete={onDelete}
								onRename={onRename}
								isManager={isManager}
								draggable={canReorder}
								isDragging={draggingId === snapshot.id}
								isDragTarget={dragOverId === snapshot.id && draggingId !== snapshot.id}
								masonryTile='compact'
								colSpan={colSpan}
								nextColSpan={nextColSpan}
								gridColumns={gridColumns}
								canResizeWidth={canResizeWidth}
								onCycleWidth={() => handleCycleColSpan(snapshot.id, colSpan)}
								onResizePointerDown={(event) => handleResizePointerDown(event, snapshot.id)}
								onDragHandleStart={(event) => handleDragStart(event, snapshot.id)}
								onDragHandleEnd={handleDragEnd}
								surface='dashboard'
								onFullscreen={handleOpenFullscreen}
								useSharedFullscreenModal
								onEdit={resolveCardEditHandler(snapshot)}
							/>
						</div>
					);
				})}
				{draggingId ? (
					<div
						className={[
							'dashboard-drag-append-zone',
							'statistics-persisted-charts__append-zone',
							dragOverId === DASHBOARD_DRAG_APPEND_TARGET_ID ? 'dashboard-drag-append-zone--active' : null,
						]
							.filter(Boolean)
							.join(' ')}
						onDragEnter={(event) => event.preventDefault()}
						onDragOver={handleAppendDragOver}
						onDrop={handleAppendDrop}
					>
						{dragOverId === DASHBOARD_DRAG_APPEND_TARGET_ID ? (
							<div className='dashboard-layout-section__insert-line' aria-hidden='true' />
						) : null}
					</div>
				) : null}
			</div>
			<HomeDashboardChartsFullscreenModal
				show={Boolean(fullscreenChartId)}
				onHide={handleCloseFullscreen}
				charts={charts}
				activeChartId={fullscreenChartId}
				onActiveChartChange={setFullscreenChartId}
				bundleByChartId={bundleByChartId}
				isManager={isManager}
				showViewMoreLink
				onDrillChange={handleModalDrillChange}
			/>
		</section>
	);
};

HomeDashboardChartsGrid.propTypes = {
	charts: PropTypes.arrayOf(PropTypes.object),
	onDelete: PropTypes.func.isRequired,
	onRename: PropTypes.func,
	onReorder: PropTypes.func,
	onResizeColSpan: PropTypes.func,
	onOpenChartWorkbench: PropTypes.func,
	onEditSaved: PropTypes.func,
	isManager: PropTypes.bool,
	maxCharts: PropTypes.number,
	actionMessage: PropTypes.string,
	isChartsLoading: PropTypes.bool,
	dataRefreshToken: PropTypes.number,
	userId: PropTypes.string,
};

export default memo(HomeDashboardChartsGrid);
